import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useDispatch} from 'react-redux';
import {prepareCsvImport, processIdentifyCSVFile} from 'stores/business/taskListPicklistBusiness';
import {CSVImportPlateColumnPreference, CSVModalStep, FastRobotErrorcode} from 'constants/dataEnum';
import {getErrorNumber} from 'stores/business/commonBusiness';
import CSVImportFileUploadModal from './CSVImportFileUploadModal';
import CSVImportPlateColumnPreferenceModal from './CSVImportPlateColumnPreferenceModal';
import CSVImportInvalidCSVModal from './CSVImportInvalidCSVModal';

const defaultErrorData = {
  errorNumber: null,
  errorCode: FastRobotErrorcode.NoError,
  errorDetail: null,
};

function CSVImportModalContainer(props) {
  const {showModal, onCloseModal, onConfirmCsvImport} = props;

  const dispatch = useDispatch();

  const fileInputRef = useRef(null);
  const csvDetailDataRef = useRef({});

  const [csvModalStep, setCSVModalStep] = useState(undefined);
  const [isLoadingCsv, setIsLoadingCsv] = useState(false);
  const [errorData, setErrorData] = useState(defaultErrorData);

  useEffect(() => {
    if (showModal && !csvModalStep) setCSVModalStep(CSVModalStep.FILE_UPLOAD);
  }, [showModal, csvModalStep]);

  const resetCSVImportModalContainer = () => {
    setCSVModalStep(undefined);
    setIsLoadingCsv(false);
    setErrorData(defaultErrorData);
    csvDetailDataRef.current = {};
  };

  const finishCSVImportModal = (platePreference) => {
    const {csvStr, csvPlate} = csvDetailDataRef.current;
    const totalDeckIdNeedToDefine = dispatch(prepareCsvImport(csvStr, csvPlate, platePreference));
    resetCSVImportModalContainer();
    onConfirmCsvImport(totalDeckIdNeedToDefine);
  };

  const processV1CSVFile = () => {
    // v1 csv only process plate barcode
    finishCSVImportModal(CSVImportPlateColumnPreference.PLATE_BARCODE);
  };

  const showInvalidCSVModal = async ({errorCode, errorDetail}) => {
    const errorNumber = await getErrorNumber(errorCode);
    setErrorData({errorNumber, errorCode, errorDetail});
    setCSVModalStep(CSVModalStep.INVALID_CSV);
  };

  const identifyAndProcessCSVFile = async (csvFile) => {
    const csvDetail = await dispatch(processIdentifyCSVFile(csvFile));

    // store csv detail data to use later on confirm
    csvDetailDataRef.current = {...csvDetail};
    const {csvVersion, errorCode, errorDetail, isValidAgainstProtocol} = csvDetailDataRef.current;

    if (errorCode !== FastRobotErrorcode.NoError) {
      showInvalidCSVModal({errorCode, errorDetail});
      return;
    }

    if (!isValidAgainstProtocol) {
      // using FastRobotErrorcode.INVALID_CONTENT following old implementation
      showInvalidCSVModal({
        errorCode: FastRobotErrorcode.INVALID_CONTENT,
        errorDetail: FastRobotErrorcode.INVALID_CONTENT,
      });
      return;
    }

    switch (csvVersion) {
      case 1:
        processV1CSVFile();
        break;
      case 2:
        setCSVModalStep(CSVModalStep.PLATE_COLUMN_PREFERENCE);
        break;
      default:
        break;
    }
    // Stop here: don't put any code after this line!
  };

  const handleUploadCSVFile = async (e) => {
    if (!e.target.files?.length) return;
    const csvFile = e.target.files[0];

    setIsLoadingCsv(true);
    await identifyAndProcessCSVFile(csvFile);
    setIsLoadingCsv(false);
  };

  const handleCloseModal = (args) => {
    resetCSVImportModalContainer();
    onCloseModal(args);
  };

  const handleChangeInputFile = (e) => {
    handleUploadCSVFile(e);
    fileInputRef.current.value = '';
  };

  const handleOpenFileInput = () => {
    fileInputRef.current.click();
  };

  const handleConfirmPlatePreference = (platePreference) => {
    finishCSVImportModal(platePreference);
  };

  if (!showModal) return null;

  const renderCSVImportModal = () => {
    switch (csvModalStep) {
      case CSVModalStep.FILE_UPLOAD:
        return (
          <CSVImportFileUploadModal
            onCloseModal={handleCloseModal}
            isLoadingUploadCsv={isLoadingCsv}
            onOpenFileInput={handleOpenFileInput}
          />
        );
      case CSVModalStep.PLATE_COLUMN_PREFERENCE:
        return (
          <CSVImportPlateColumnPreferenceModal
            onCloseModal={handleCloseModal}
            onConfirmPlatePreference={handleConfirmPlatePreference}
          />
        );
      case CSVModalStep.INVALID_CSV:
        return (
          <CSVImportInvalidCSVModal
            onAbort={handleCloseModal}
            isLoadingUploadCsv={isLoadingCsv}
            onOpenFileInput={handleOpenFileInput}
            errorData={errorData}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {renderCSVImportModal()}
      <input
        ref={fileInputRef}
        type="file"
        name="import-file"
        onChange={handleChangeInputFile}
        accept=".csv"
        multiple={false}
        style={{position: 'absolute', opacity: 0, pointerEvents: 'none'}}
      />
    </>
  );
}

CSVImportModalContainer.propTypes = {
  showModal: PropTypes.bool.isRequired,
  onCloseModal: PropTypes.func.isRequired,
  onConfirmCsvImport: PropTypes.func.isRequired,
};

export default React.memo(CSVImportModalContainer);
